import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { projectAPI, inventoryAPI, leadAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger 
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Plus, Building2, MapPin, Home, Users, Briefcase, IndianRupee,
  Check, X, Clock, Eye, ChevronRight, Layers, Calendar, Star,
  Phone, Mail, FileText, Globe, Award, Settings
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const ProjectsPage = () => {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialog, setDetailDialog] = useState({ open: false, project: null, stats: null });
  
  const [newProject, setNewProject] = useState({
    name: '',
    slug: '',
    description: '',
    location: '',
    city: '',
    state: '',
    bhk_options: '',
    amenities: '',
    rera_number: '',
    price_range_min: '',
    price_range_max: '',
  });

  const canCreate = hasRole(['super_admin', 'builder_admin']);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const response = await projectAPI.list();
      setProjects(response.data);
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const openProjectDetails = async (project) => {
    try {
      // Load project stats
      const [inventoryRes, leadsRes] = await Promise.all([
        inventoryAPI.list(project.id).catch(() => ({ data: [] })),
        leadAPI.list({ project_id: project.id }).catch(() => ({ data: [] }))
      ]);
      
      const inventory = inventoryRes.data || [];
      // Handle paginated response - extract leads array
      const leadsData = leadsRes.data?.leads || leadsRes.data;
      const leads = Array.isArray(leadsData) ? leadsData : [];
      
      const stats = {
        totalUnits: inventory.length,
        availableUnits: inventory.filter(u => u.status === 'available').length,
        soldUnits: inventory.filter(u => u.status === 'sold').length,
        blockedUnits: inventory.filter(u => u.status === 'blocked').length,
        totalLeads: leads.length,
        bookedLeads: leads.filter(l => l.status === 'booked').length,
        activeLeads: leads.filter(l => !['booked', 'lost', 'not_interested'].includes(l.status)).length,
        inventory: inventory.slice(0, 10), // Show first 10 units
        recentLeads: leads.slice(0, 5) // Show first 5 leads
      };
      
      setDetailDialog({ open: true, project, stats });
    } catch (error) {
      toast.error('Failed to load project details');
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...newProject,
        bhk_options: newProject.bhk_options.split(',').map(s => s.trim()).filter(Boolean),
        amenities: newProject.amenities.split(',').map(s => s.trim()).filter(Boolean),
        price_range: {
          min: newProject.price_range_min ? parseFloat(newProject.price_range_min) : null,
          max: newProject.price_range_max ? parseFloat(newProject.price_range_max) : null,
        }
      };
      delete data.price_range_min;
      delete data.price_range_max;
      
      await projectAPI.create(data);
      toast.success('Project created successfully');
      setDialogOpen(false);
      setNewProject({
        name: '', slug: '', description: '', location: '', city: '', state: '',
        bhk_options: '', amenities: '', rera_number: '', price_range_min: '', price_range_max: '',
      });
      loadProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create project');
    }
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)} L`;
    return `₹${amount.toLocaleString()}`;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="projects-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Building2 className="h-7 w-7 text-violet-600" />
            Projects
          </h1>
          <p className="text-muted-foreground">{projects.length} projects available</p>
        </div>
        
        {canCreate && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="btn-add-project">
                <Plus className="h-4 w-4 mr-2" />
                Add Project
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add New Project</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateProject} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="project-name">Project Name *</Label>
                  <Input
                    id="project-name"
                    value={newProject.name}
                    onChange={(e) => {
                      setNewProject({ 
                        ...newProject, 
                        name: e.target.value,
                        slug: generateSlug(e.target.value)
                      });
                    }}
                    required
                    data-testid="input-project-name"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="project-slug">URL Slug *</Label>
                  <Input
                    id="project-slug"
                    value={newProject.slug}
                    onChange={(e) => setNewProject({ ...newProject, slug: e.target.value })}
                    required
                    data-testid="input-project-slug"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="project-desc">Description</Label>
                  <Textarea
                    id="project-desc"
                    value={newProject.description}
                    onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                    rows={3}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="project-location">Location</Label>
                    <Input
                      id="project-location"
                      placeholder="e.g., Whitefield"
                      value={newProject.location}
                      onChange={(e) => setNewProject({ ...newProject, location: e.target.value })}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="project-city">City</Label>
                    <Input
                      id="project-city"
                      placeholder="e.g., Bangalore"
                      value={newProject.city}
                      onChange={(e) => setNewProject({ ...newProject, city: e.target.value })}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="project-rera">RERA Number</Label>
                  <Input
                    id="project-rera"
                    placeholder="e.g., PRM/KA/RERA/1234567890"
                    value={newProject.rera_number}
                    onChange={(e) => setNewProject({ ...newProject, rera_number: e.target.value })}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Price From (₹)</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 5000000"
                      value={newProject.price_range_min}
                      onChange={(e) => setNewProject({ ...newProject, price_range_min: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Price To (₹)</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 15000000"
                      value={newProject.price_range_max}
                      onChange={(e) => setNewProject({ ...newProject, price_range_max: e.target.value })}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="project-bhk">BHK Options (comma-separated)</Label>
                  <Input
                    id="project-bhk"
                    placeholder="e.g., 2 BHK, 3 BHK, 4 BHK"
                    value={newProject.bhk_options}
                    onChange={(e) => setNewProject({ ...newProject, bhk_options: e.target.value })}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="project-amenities">Amenities (comma-separated)</Label>
                  <Input
                    id="project-amenities"
                    placeholder="e.g., Pool, Gym, Clubhouse"
                    value={newProject.amenities}
                    onChange={(e) => setNewProject({ ...newProject, amenities: e.target.value })}
                  />
                </div>
                
                <Button type="submit" className="w-full" data-testid="btn-submit-project">
                  Create Project
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Projects Grid */}
      {projects.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Building2 className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium">No projects yet</p>
            <p className="text-muted-foreground">
              {canCreate ? 'Create your first project to get started' : 'No projects have been assigned yet'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map((project) => (
            <Card 
              key={project.id} 
              className="overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => openProjectDetails(project)}
            >
              <div className="h-32 bg-gradient-to-br from-violet-900 to-slate-900 flex items-center justify-center relative">
                <Building2 className="h-16 w-16 text-violet-400/30" />
                {project.is_active !== false && (
                  <Badge className="absolute top-3 right-3 bg-emerald-500 text-white">Active</Badge>
                )}
              </div>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-semibold text-lg">{project.name}</h3>
                </div>
                
                {project.location && (
                  <p className="flex items-center gap-1 text-sm text-muted-foreground mb-2">
                    <MapPin className="h-3 w-3" />
                    {project.location}{project.city ? `, ${project.city}` : ''}
                  </p>
                )}
                
                {project.price_range && (project.price_range.min || project.price_range.max) && (
                  <p className="flex items-center gap-1 text-sm text-emerald-600 font-medium mb-2">
                    <IndianRupee className="h-3 w-3" />
                    {formatCurrency(project.price_range.min)} - {formatCurrency(project.price_range.max)}
                  </p>
                )}
                
                {project.bhk_options?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {project.bhk_options.map((bhk) => (
                      <span key={bhk} className="px-2 py-0.5 bg-violet-100 text-violet-700 rounded text-xs flex items-center gap-1">
                        <Home className="h-3 w-3" />
                        {bhk}
                      </span>
                    ))}
                  </div>
                )}
                
                <div className="flex flex-wrap items-center gap-1 pt-2 border-t">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      openProjectDetails(project);
                    }}
                    data-testid={`btn-quick-view-${project.id}`}
                  >
                    <Eye className="h-3 w-3 mr-1" />
                    View
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/builder/leads?project=${project.id}`);
                    }}
                    data-testid={`btn-leads-${project.id}`}
                  >
                    <Users className="h-3 w-3 mr-1" />
                    Leads
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/builder/inventory?project=${project.id}`);
                    }}
                    data-testid={`btn-inventory-${project.id}`}
                  >
                    <Layers className="h-3 w-3 mr-1" />
                    Inventory
                  </Button>
                  {canCreate && (
                    <Button 
                      variant="default" 
                      size="sm" 
                      className="text-xs ml-auto"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/builder/projects/${project.id}`);
                      }}
                      data-testid={`btn-manage-${project.id}`}
                    >
                      <Settings className="h-3 w-3 mr-1" />
                      Manage
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Project Detail Dialog */}
      <Dialog open={detailDialog.open} onOpenChange={(open) => setDetailDialog({ open, project: null, stats: null })}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {detailDialog.project && (
            <ProjectDetailView 
              project={detailDialog.project} 
              stats={detailDialog.stats}
              formatCurrency={formatCurrency}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Project Detail View Component
const ProjectDetailView = ({ project, stats, formatCurrency }) => {
  const [activeTab, setActiveTab] = useState('overview');
  
  const soldPercentage = stats?.totalUnits > 0 
    ? ((stats.soldUnits / stats.totalUnits) * 100).toFixed(0) 
    : 0;

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Building2 className="h-6 w-6 text-violet-600" />
          {project.name}
        </DialogTitle>
      </DialogHeader>
      
      <div className="space-y-6 mt-4">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-violet-900 to-slate-900 rounded-xl p-6 text-white">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold mb-2">{project.name}</h2>
              <p className="flex items-center gap-2 text-violet-200">
                <MapPin className="h-4 w-4" />
                {project.location}{project.city ? `, ${project.city}` : ''}{project.state ? `, ${project.state}` : ''}
              </p>
              {project.rera_number && (
                <p className="flex items-center gap-2 text-violet-300 text-sm mt-1">
                  <Award className="h-4 w-4" />
                  RERA: {project.rera_number}
                </p>
              )}
            </div>
            <div className="text-right">
              {project.price_range && (project.price_range.min || project.price_range.max) && (
                <div>
                  <p className="text-violet-300 text-sm">Starting from</p>
                  <p className="text-2xl font-bold text-amber-400">
                    {formatCurrency(project.price_range.min)}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <Layers className="h-6 w-6 mx-auto mb-2 text-violet-600" />
              <p className="text-2xl font-bold">{stats?.totalUnits || 0}</p>
              <p className="text-xs text-muted-foreground">Total Units</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Check className="h-6 w-6 mx-auto mb-2 text-emerald-600" />
              <p className="text-2xl font-bold text-emerald-600">{stats?.availableUnits || 0}</p>
              <p className="text-xs text-muted-foreground">Available</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Users className="h-6 w-6 mx-auto mb-2 text-blue-600" />
              <p className="text-2xl font-bold">{stats?.totalLeads || 0}</p>
              <p className="text-xs text-muted-foreground">Total Leads</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Briefcase className="h-6 w-6 mx-auto mb-2 text-amber-600" />
              <p className="text-2xl font-bold text-amber-600">{stats?.bookedLeads || 0}</p>
              <p className="text-xs text-muted-foreground">Bookings</p>
            </CardContent>
          </Card>
        </div>

        {/* Sales Progress */}
        {stats?.totalUnits > 0 && (
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">Sales Progress</span>
                <span className="text-sm text-muted-foreground">{soldPercentage}% sold</span>
              </div>
              <Progress value={parseFloat(soldPercentage)} className="h-3" />
              <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                <span>{stats.soldUnits} sold</span>
                <span>{stats.blockedUnits} blocked</span>
                <span>{stats.availableUnits} available</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="inventory">Inventory ({stats?.totalUnits || 0})</TabsTrigger>
            <TabsTrigger value="leads">Leads ({stats?.totalLeads || 0})</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-4 space-y-4">
            {/* Description */}
            {project.description && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    About
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{project.description}</p>
                </CardContent>
              </Card>
            )}

            {/* BHK Options */}
            {project.bhk_options?.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Home className="h-5 w-5" />
                    Unit Types
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {project.bhk_options.map((bhk) => (
                      <Badge key={bhk} variant="secondary" className="px-4 py-2 text-sm">
                        {bhk}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Amenities */}
            {project.amenities?.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Star className="h-5 w-5" />
                    Amenities
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {project.amenities.map((amenity) => (
                      <div key={amenity} className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-emerald-600" />
                        <span className="text-sm">{amenity}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="inventory" className="mt-4">
            <Card>
              <CardContent className="p-4">
                {!stats?.inventory?.length ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Layers className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No inventory units added yet</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="grid grid-cols-5 text-xs font-semibold text-muted-foreground py-2 border-b">
                      <span>Unit</span>
                      <span>Floor</span>
                      <span>Type</span>
                      <span>Area</span>
                      <span>Status</span>
                    </div>
                    {stats.inventory.map((unit) => (
                      <div key={unit.id} className="grid grid-cols-5 text-sm py-2 border-b">
                        <span className="font-medium">{unit.unit_number}</span>
                        <span>Floor {unit.floor}</span>
                        <span>{unit.bhk_type}</span>
                        <span>{unit.carpet_area} sq.ft</span>
                        <Badge className={
                          unit.status === 'available' ? 'bg-emerald-100 text-emerald-700' :
                          unit.status === 'sold' ? 'bg-blue-100 text-blue-700' :
                          'bg-amber-100 text-amber-700'
                        }>
                          {unit.status}
                        </Badge>
                      </div>
                    ))}
                    {stats.totalUnits > 10 && (
                      <p className="text-center text-sm text-muted-foreground pt-2">
                        Showing 10 of {stats.totalUnits} units
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="leads" className="mt-4">
            <Card>
              <CardContent className="p-4">
                {!stats?.recentLeads?.length ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No leads for this project yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {stats.recentLeads.map((lead) => (
                      <div key={lead.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <Users className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium">{lead.name}</p>
                            <p className="text-sm text-muted-foreground">{lead.phone}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge className={
                            lead.status === 'booked' ? 'bg-emerald-100 text-emerald-700' :
                            lead.status === 'qualified' ? 'bg-blue-100 text-blue-700' :
                            'bg-slate-100 text-slate-600'
                          }>
                            {lead.status}
                          </Badge>
                          {lead.bhk_preference && (
                            <p className="text-xs text-muted-foreground mt-1">{lead.bhk_preference}</p>
                          )}
                        </div>
                      </div>
                    ))}
                    {stats.totalLeads > 5 && (
                      <p className="text-center text-sm text-muted-foreground pt-2">
                        Showing 5 of {stats.totalLeads} leads
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
};

export default ProjectsPage;
